<template>
  <div
    v-if="shouldLockContent"
    class="fixed inset-0 z-40 bg-black/40"
    aria-hidden="true"
  />

  <div v-if="shouldShowBanner" class="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6">
    <div
      v-if="shouldShowBanner"
      class="mx-auto max-w-5xl border border-gray-200 bg-white shadow-lg"
      role="region"
      :aria-label="$t('cookie_consent.banner_label')"
    >
      <div class="flex flex-col gap-5 p-5 md:flex-row md:items-center md:justify-between">
        <div class="max-w-2xl">
          <p class="text-sm font-medium uppercase tracking-[0.2em] text-gray-500">
            {{ $t('cookie_consent.eyebrow') }}
          </p>
          <h2 class="mt-2 text-lg font-medium text-gray-900">
            {{ $t('cookie_consent.title') }}
          </h2>
          <p class="mt-2 text-sm leading-6 text-gray-600">
            {{ $t('cookie_consent.description') }}
            <router-link to="/privacy-policy" class="font-medium text-gray-900 underline underline-offset-4">
              {{ $t('cookie_consent.privacy_link') }}
            </router-link>
          </p>
        </div>

        <div class="flex flex-col gap-2 sm:flex-row md:flex-col lg:flex-row">
          <button
            type="button"
            class="border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50"
            @click="openSettings"
          >
            {{ $t('cookie_consent.settings') }}
          </button>
          <button
            type="button"
            class="border border-gray-900 px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-900 hover:text-white"
            @click="rejectOptional"
          >
            {{ $t('cookie_consent.reject_optional') }}
          </button>
          <button
            type="button"
            class="bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
            @click="acceptAll"
          >
            {{ $t('cookie_consent.accept_all') }}
          </button>
        </div>
      </div>
    </div>

  </div>

  <div
    v-if="showSettings"
    class="fixed inset-0 z-[60] flex items-end justify-center px-4 py-6 sm:items-center"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('cookie_consent.settings_title')"
  >
    <div class="w-full max-w-2xl bg-white shadow-xl">
      <div class="border-b border-gray-200 px-5 py-4">
        <div class="flex items-start justify-between gap-4">
          <div>
            <p class="text-sm font-medium uppercase tracking-[0.2em] text-gray-500">
              {{ $t('cookie_consent.eyebrow') }}
            </p>
            <h2 class="mt-2 text-xl font-medium text-gray-900">
              {{ $t('cookie_consent.settings_title') }}
            </h2>
          </div>
          <button
            type="button"
            class="px-2 py-1 text-sm text-gray-500 transition-colors hover:text-gray-900"
            :aria-label="$t('common.close')"
            @click="closeSettings"
          >
            &times;
          </button>
        </div>
      </div>

      <div class="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-5">
        <section class="border border-gray-200 p-4">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3 class="font-medium text-gray-900">{{ $t('cookie_consent.categories.essential.title') }}</h3>
              <p class="mt-1 text-sm leading-6 text-gray-600">
                {{ $t('cookie_consent.categories.essential.description') }}
              </p>
            </div>
            <span class="shrink-0 text-sm font-medium text-gray-500">
              {{ $t('cookie_consent.always_on') }}
            </span>
          </div>
        </section>

        <section class="border border-gray-200 p-4">
          <label class="flex cursor-pointer items-start justify-between gap-4">
            <span>
              <span class="block font-medium text-gray-900">{{ $t('cookie_consent.categories.analytics.title') }}</span>
              <span class="mt-1 block text-sm leading-6 text-gray-600">
                {{ $t('cookie_consent.categories.analytics.description') }}
              </span>
            </span>
            <input
              v-model="analyticsEnabled"
              type="checkbox"
              class="mt-1 h-5 w-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            >
          </label>
        </section>

        <section class="border border-gray-200 p-4">
          <label class="flex cursor-pointer items-start justify-between gap-4">
            <span>
              <span class="block font-medium text-gray-900">{{ $t('cookie_consent.categories.marketing.title') }}</span>
              <span class="mt-1 block text-sm leading-6 text-gray-600">
                {{ $t('cookie_consent.categories.marketing.description') }}
              </span>
            </span>
            <input
              v-model="marketingEnabled"
              type="checkbox"
              class="mt-1 h-5 w-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            >
          </label>
        </section>
      </div>

      <div class="flex flex-col gap-2 border-t border-gray-200 px-5 py-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          class="border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50"
          @click="rejectOptional"
        >
          {{ $t('cookie_consent.reject_optional') }}
        </button>
        <button
          type="button"
          class="border border-gray-900 px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-900 hover:text-white"
          @click="saveSettings"
        >
          {{ $t('cookie_consent.save_settings') }}
        </button>
        <button
          type="button"
          class="bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
          @click="acceptAll"
        >
          {{ $t('cookie_consent.accept_all') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useCookieConsent } from '@/composables/useCookieConsent'

const {
  categories,
  hasConsent,
  isSettingsOpen: showSettings,
  acceptAll: acceptAllConsent,
  rejectOptional: rejectOptionalConsent,
  savePreferences,
  openSettings,
  closeSettings,
} = useCookieConsent()

const analyticsEnabled = ref(categories.value.analytics)
const marketingEnabled = ref(categories.value.marketing)

const shouldShowBanner = computed(() => !hasConsent.value)
const shouldLockContent = computed(() => shouldShowBanner.value || showSettings.value)

watch(showSettings, (isOpen) => {
  if (isOpen) {
    analyticsEnabled.value = categories.value.analytics
    marketingEnabled.value = categories.value.marketing
  }
})

watch(shouldLockContent, (isLocked) => {
  document.body.style.overflow = isLocked ? 'hidden' : ''
}, { immediate: true })

onUnmounted(() => {
  document.body.style.overflow = ''
})

const acceptAll = () => {
  acceptAllConsent()
  closeSettings()
}

const rejectOptional = () => {
  rejectOptionalConsent()
  closeSettings()
}

const saveSettings = () => {
  savePreferences({
    analytics: analyticsEnabled.value,
    marketing: marketingEnabled.value,
  })
  closeSettings()
}
</script>
